import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { UploadCloud, Film, Check } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const TASKS = [
  { key: 'shorten', label: 'Shorten video' },
  { key: 'voice', label: 'Replace voice' },
  { key: 'music', label: 'Add music' },
  { key: 'captions', label: 'Add captions' },
];
const LENGTHS = ['3', '5', '8', '10', '12', '15'];
const AI_MODELS = [
  { id: 'gpt-5.4', label: 'GPT-5.4', vendor: 'OpenAI', desc: 'Balanced, great all-rounder' },
  { id: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro', vendor: 'Google', desc: 'Strong reasoning & structure' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', vendor: 'Anthropic', desc: 'Nuanced writing & pacing' },
];

export default function CreateVideo() {
  const nav = useNavigate();
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [presets, setPresets] = useState([]);
  const [preset, setPreset] = useState('custom');
  const [tasks, setTasks] = useState({ shorten: true, voice: true, music: true, captions: true });
  const [target, setTarget] = useState('10');
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [url, setUrl] = useState('');
  const [model, setModel] = useState('gpt-5.4');

  useEffect(() => { api.get('/templates').then(({ data }) => setPresets(data)).catch(() => {}); }, []);

  const pick = (f) => {
    if (!f) return;
    const ok = /\.(mp4|mov|mkv|webm|m4v|avi)$/i.test(f.name);
    if (!ok) { toast.error('Please use MP4, MOV, MKV or WebM'); return; }
    setFile(f);
  };

  const create = async () => {
    if (url.trim()) return importLink();
    if (!file) { toast.error('Upload a video or paste a link first'); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('preset', preset);
    fd.append('ai_model', model);
    fd.append('title', file.name.replace(/\.[^.]+$/, ''));
    try {
      const { data } = await api.post('/projects/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      localStorage.setItem(`te_intent_${data.id}`, JSON.stringify({ tasks, target: Number(target) }));
      toast.success('Uploaded — analyzing your video');
      nav(`/app/studio/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  const importLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/projects/import', { url: url.trim(), preset, title: '', ai_model: model });
      localStorage.setItem(`te_intent_${data.id}`, JSON.stringify({ tasks, target: Number(target) }));
      toast.success('Imported — analyzing your video');
      nav(`/app/studio/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not import that link');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight mb-2">Create your video</h1>
      <p className="text-slate-400 mb-8">Upload once — AI handles the first 80% of the edit.</p>

      <div
        data-testid="dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${drag ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-white/15 bg-[#0B0C12] hover:border-white/30'}`}>
        <input ref={inputRef} type="file" accept="video/*,.mkv,.webm,.mov,.mp4" className="hidden"
          onChange={(e) => pick(e.target.files?.[0])} data-testid="file-input" />
        {file ? (
          <div>
            <div className="grid place-items-center w-14 h-14 rounded-xl bg-[#FF4D00]/15 mx-auto mb-3"><Film className="w-7 h-7 text-[#FF884D]" /></div>
            <div className="font-medium">{file.name}</div>
            <div className="text-sm text-slate-500 mt-1">{(file.size / 1024 / 1024).toFixed(1)} MB · click to change</div>
          </div>
        ) : (
          <div>
            <UploadCloud className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <div className="font-medium">Drop video here <span className="text-slate-500">or click to upload</span></div>
            <div className="text-xs text-slate-500 mt-2 font-mono">MP4 · MOV · MKV · WebM</div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="h-px bg-white/10 flex-1" />
        <span className="text-xs text-slate-500 font-mono">OR IMPORT FROM A LINK</span>
        <div className="h-px bg-white/10 flex-1" />
      </div>
      <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="link-input"
        placeholder="Paste a YouTube URL or direct video link (https://…)"
        className="mt-3 bg-[#0B0C12] border-white/12 h-12" />
      <p className="text-xs text-slate-500 mt-1.5">YouTube import downloads up to 720p · direct .mp4/.mov links also work.</p>

      <div className="mt-8">
        <div className="text-sm text-slate-400 mb-3">Choose the AI brain</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {AI_MODELS.map((m) => (
            <button key={m.id} data-testid={`ai-model-${m.id}`} onClick={() => setModel(m.id)}
              className={`text-left rounded-xl border p-4 transition-colors ${model === m.id ? 'border-[#FF4D00] bg-[#FF4D00]/10' : 'border-white/10 bg-[#12141D] hover:border-white/25'}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{m.label}</div>
                {model === m.id && <Check className="w-4 h-4 text-[#FF884D]" />}
              </div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{m.vendor}</div>
              <div className="text-xs text-slate-400 mt-1.5">{m.desc}</div>
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">Powers analysis, smart-cut editing, B-roll prompts & YouTube copy. You can switch it later in the editor.</p>
      </div>

      <div className="mt-8">
        <div className="text-sm text-slate-400 mb-3">Choose a style preset</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {presets.map((p) => (
            <button key={p.id} data-testid={`preset-${p.id}`} onClick={() => setPreset(p.id)}
              className={`text-left rounded-xl border p-4 transition-colors ${preset === p.id ? 'border-[#FF4D00] bg-[#FF4D00]/10' : 'border-white/10 bg-[#12141D] hover:border-white/25'}`}>
              <div className="text-2xl mb-1">{p.emoji}</div>
              <div className="text-sm font-semibold">{p.name}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <div className="text-sm text-slate-400 mb-3">What do you want AI to do?</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {TASKS.map((t) => (
            <button key={t.key} data-testid={`task-${t.key}`} onClick={() => setTasks((x) => ({ ...x, [t.key]: !x[t.key] }))}
              className={`flex items-center gap-3 rounded-xl border p-4 transition-colors ${tasks[t.key] ? 'border-[#8B5CF6]/50 bg-[#8B5CF6]/10' : 'border-white/10 bg-[#12141D]'}`}>
              <span className={`grid place-items-center w-5 h-5 rounded ${tasks[t.key] ? 'bg-[#8B5CF6]' : 'bg-white/10'}`}>
                {tasks[t.key] && <Check className="w-3.5 h-3.5 text-white" />}
              </span>
              <span className="text-sm">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between rounded-xl border border-white/10 bg-[#12141D] p-4">
        <span className="text-sm text-slate-300">Target length</span>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger className="w-32 bg-[#0B0C12] border-white/12" data-testid="target-length-select"><SelectValue /></SelectTrigger>
          <SelectContent>{LENGTHS.map((l) => <SelectItem key={l} value={l}>{l} min</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Button data-testid="create-video-submit" onClick={create} disabled={busy || (!file && !url.trim())} className="w-full mt-8 rounded-full h-12 font-semibold text-base">
        {busy ? 'Working…' : (url.trim() ? 'Import & Create Video' : 'Create Video')}
      </Button>
    </div>
  );
}
